import {
  usePlaybackCurrentTs,
  usePlaybackIsPlaying,
  usePlaybackPause,
  usePlaybackPlay,
  usePlaybackResume,
} from '@app/core/playback/hooks/usePlayback';
import debugFn from 'debug';
import { useCallback } from 'react';
import { useCursor, useFocus } from '../../stores/stream-screen.store';
import { useStreamSelection } from '../../stores/useStreamSelection';

const debug = debugFn('playback');

/**
 * Hook to play from the current cursor position
 */
export function usePlayFromCursor() {
  const cursorTs = useCursor();
  const play = usePlaybackPlay();

  return useCallback(async () => {
    if (!cursorTs) {
      return;
    }

    debug('play from cursor at %s', new Date(cursorTs).toISOString());

    play({
      startTs: cursorTs,
      playAfterSeek: true,
    });
  }, [cursorTs, play]);
}

/**
 * Hook to play the current selection
 */
export function usePlaySelection() {
  const { selectionRange, selectionExists } = useStreamSelection();
  const play = usePlaybackPlay();

  return useCallback(async () => {
    if (!selectionExists) {
      return;
    }

    play({
      startTs: selectionRange.from,
      stopTs: selectionRange.to,
    });
  }, [selectionExists, selectionRange, play]);
}

/**
 * Hook to play from the focus start
 */
export function usePlayFromFocusStart() {
  const focusRange = useFocus();
  const play = usePlaybackPlay();

  return useCallback(async () => {
    if (!focusRange.from) {
      return;
    }

    play({
      startTs: focusRange.from,
    });
  }, [focusRange.from, play]);
}

/**
 * Hook to toggle play/pause with fallback logic
 */
export function useStreamGlobalPlayPause() {
  const isPlaying = usePlaybackIsPlaying();
  const currentTs = usePlaybackCurrentTs();
  const cursorTs = useCursor();
  const { selectionExists } = useStreamSelection();
  const focusRange = useFocus();
  const pause = usePlaybackPause();
  const resume = usePlaybackResume();
  const playFromCursor = usePlayFromCursor();
  const playSelection = usePlaySelection();
  const playFromFocusStart = usePlayFromFocusStart();

  return useCallback(() => {
    // if audio is playing, just pause
    if (isPlaying) {
      pause();
      return;
    }

    // if not playing and there is a cursor, play from cursor
    if (cursorTs) {
      playFromCursor();
      return;
    }

    // if not playing and there is a selection, play from start
    if (selectionExists) {
      playSelection();
      return;
    }

    // if no cursor and no selection but playhead exists, resume
    if (currentTs) {
      resume();
      return;
    }

    // otherwise play from focus start if focus exists
    if (focusRange.from) {
      playFromFocusStart();
    }
  }, [
    isPlaying,
    currentTs,
    cursorTs,
    selectionExists,
    focusRange.from,
    pause,
    resume,
    playFromCursor,
    playSelection,
    playFromFocusStart,
  ]);
}
